import { ApiService } from "./apiService";
import { routes } from "./paths";

// Values confirmed by the backend team; the Swagger file lists only the numbers.
export enum ApiNotificationStatus {
  Draft = 1,
  Scheduled = 2,
  Sending = 3,
  Sent = 4,
  Failed = 5,
}

export enum ApiNotificationTargetType {
  All = 1,
  Segment = 2,
  UserIds = 3,
  ExcludeUserIds = 4,
  Vendors = 5,
  Users = 6,
  Emails = 7,
  Location = 8,
}

export interface CreatePushNotificationCommand {
  title: string;
  body: string;
  targetType: ApiNotificationTargetType.Vendors | ApiNotificationTargetType.Users | ApiNotificationTargetType.Emails | ApiNotificationTargetType.Location;
  emails?: string[];
  city?: string;
  province?: string;
  locationLatitude?: number;
  locationLongitude?: number;
  locationRadiusKm?: number;
}

export interface CreatePushNotificationResponse {
  id: string;
  success: boolean;
  message: string | null;
}

export interface NotificationDto {
  id: string;
  title: string | null;
  body: string | null;
  imageUrl: string | null;
  deepLink: string | null;
  targetType: ApiNotificationTargetType;
  status: ApiNotificationStatus;
  scheduledAt: string | null;
  sentAt: string | null;
  totalDevices: number;
  sentCount: number;
  deliveredCount: number;
  openedCount: number;
  failedCount: number;
  createdDate: string;
  createdBy: string | null;
}

export interface NotificationDetailDto extends NotificationDto {
  targetFilter: string | null;
  templateId: string | null;
  templateName: string | null;
}

export interface NotificationAggregateBucket {
  key: string | null;
  value: string | null;
  count: number;
  image: string | null;
}

export interface NotificationAggregateResult {
  name: string | null;
  displayName: string | null;
  other: number;
  buckets: NotificationAggregateBucket[] | null;
}

export interface NotificationPageDto {
  pageNumber: number;
  pageSize: number;
  totalNumberOfPages: number;
  totalNumberOfRecords: number;
  nextPageUrl: string | null;
  prevPageUrl: string | null;
  results: NotificationDto[] | null;
  aggregateResults: NotificationAggregateResult[] | null;
}

export interface NotificationStatsDto {
  totalNotifications: number;
  totalSent: number;
  totalScheduled: number;
  totalDraft: number;
  totalDevices: number;
  activeDevices: number;
  totalDelivered: number;
  totalOpened: number;
  totalFailed: number;
  deliveryRate: number;
  openRate: number;
}

export interface NotificationListQuery {
  page?: number;
  pageSize?: number;
  status?: ApiNotificationStatus;
  searchTerm?: string;
}

const responseData = async <T,>(url: string): Promise<T> => {
  const response = await ApiService.request({ url, http_method: "get" });
  return response.data as T;
};

export const notificationApi = {
  list(query: NotificationListQuery = {}): Promise<NotificationPageDto> {
    const parameters = new URLSearchParams();
    if (query.page !== undefined) parameters.set("Page", String(query.page));
    if (query.pageSize !== undefined) parameters.set("PageSize", String(query.pageSize));
    if (query.status !== undefined) parameters.set("Status", String(query.status));
    if (query.searchTerm?.trim()) parameters.set("SearchTerm", query.searchTerm.trim());
    const search = parameters.toString();
    return responseData<NotificationPageDto>(`${routes.NOTIFICATIONS}${search ? `?${search}` : ""}`);
  },

  getById(id: string): Promise<NotificationDetailDto> {
    return responseData<NotificationDetailDto>(`${routes.NOTIFICATIONS}/${encodeURIComponent(id)}`);
  },

  stats(): Promise<NotificationStatsDto> {
    return responseData<NotificationStatsDto>(routes.NOTIFICATION_STATS);
  },

  create(command: CreatePushNotificationCommand): Promise<CreatePushNotificationResponse> {
    return ApiService.request({
      url: routes.NOTIFICATIONS,
      http_method: "post",
      data: command,
    }).then((response: { data: CreatePushNotificationResponse }) => response.data);
  },
};
